import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import { HealthGorillaLabOrderProvider } from '@medplum/health-gorilla-react';
import { useMedplumProfile } from '@medplum/react';
import type { Practitioner } from '@medplum/fhirtypes';
import { IconMicroscope, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { LAB_ORDERING_PROVIDER_ROLES } from '@bariendo/lib/constants';
import { useLabOrderForm } from './hooks/useLabOrderForm';
import classes from './LabOrderMessageBox.module.css';
import { LabOrderQuestionnaireSection } from './LabOrderQuestionnaireSection';
import {
  getEligibleOrderingProviders,
  LAST_ORDERING_PROVIDER_STORAGE_KEY,
  resolveDefaultOrderingProvider,
} from './orderingProvider';
import { usePractitionersByRoles } from '@/hooks/usePractitionersByRoles';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { getPatientName } from '@/pages/PatientTimelinePage/services/timelinePatient';

export function LabOrderMessageBox() {
  const { patientContext, messageBoxContext } = useTimelineContext();

  const patient = patientContext?.patient;
  const { setShowMessageInput, setSentMessage } = messageBoxContext;
  const profile = useMedplumProfile() as Practitioner;

  const { practitioners, loading: isLoadingProviders } = usePractitionersByRoles(LAB_ORDERING_PROVIDER_ROLES);
  const eligibleProviders = useMemo(() => getEligibleOrderingProviders(practitioners), [practitioners]);
  const providerOptions = useMemo(
    () =>
      eligibleProviders
        .map((p) => ({ value: p.id!, label: formatHumanName(p.name?.[0]) || p.id! }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [eligibleProviders]
  );

  // Undefined until the user picks; until then the default is derived, so it appears once the list loads.
  const [pickedProviderId, setPickedProviderId] = useState<string | null | undefined>();
  const orderingProvider = useMemo(() => {
    if (pickedProviderId !== undefined) {
      return eligibleProviders.find((p) => p.id === pickedProviderId);
    }
    return resolveDefaultOrderingProvider(
      profile,
      eligibleProviders,
      window.localStorage.getItem(LAST_ORDERING_PROVIDER_STORAGE_KEY)
    );
  }, [pickedProviderId, eligibleProviders, profile]);

  const {
    labOrder,
    questionnaire,
    questionnaireResponse,
    isSubmitting,
    isLoadingQuestionnaire,
    isAutoSelectingTests,
    handleQuestionnaireChange,
    handleLabsChange,
    handleTestsChange,
    handleSubmit,
  } = useLabOrderForm({
    patient,
    placedBy: profile,
    orderingProvider,
    onSuccess: () => {
      if (orderingProvider?.id) {
        window.localStorage.setItem(LAST_ORDERING_PROVIDER_STORAGE_KEY, orderingProvider.id);
      }
      setShowMessageInput(false);
      setSentMessage(true);
    },
  });

  const patientName = useMemo(() => (patient ? getPatientName(patient) : 'Unknown Patient'), [patient]);
  const orderDate = useMemo(() => dayjs().format('MM/DD/YYYY'), []);

  return (
    <Card withBorder padding={0} radius="lg" bg="white">
      <Box>
        <Stack p="xs" gap={4} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
          <Group justify="space-between" align="center">
            <Group gap="xs" align="center">
              <ThemeIcon variant="filled" radius="lg" size="lg" bg="indigo.6">
                <IconMicroscope size={16} />
              </ThemeIcon>
              <Stack w="fit-content" gap={1}>
                <Group fw={600}>
                  New Labs Order
                  <Badge variant="light" color="yellow" radius="xl" tt="uppercase" fw={600} size="xs">
                    Editing
                  </Badge>
                </Group>
                <Text className={classes.headerSubtitle}>{`${orderDate} - ${patientName}`}</Text>
              </Stack>
            </Group>
          </Group>
        </Stack>

        <Tooltip label="Close" withArrow position="top-end" openDelay={500}>
          <ActionIcon
            variant="subtle"
            size="md"
            radius="xl"
            aria-label="Close"
            onClick={() => setShowMessageInput(false)}
            style={{ position: 'absolute', top: 8, right: 8 }}
          >
            <IconX size={16} color="gray.6" />
          </ActionIcon>
        </Tooltip>

        <Box p="sm">
          {isLoadingQuestionnaire ? (
            <Group justify="center" p="md">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading form...
              </Text>
            </Group>
          ) : questionnaire && questionnaireResponse ? (
            <HealthGorillaLabOrderProvider {...labOrder}>
              <Stack gap="md">
                <LabOrderQuestionnaireSection
                  questionnaire={questionnaire}
                  questionnaireResponse={questionnaireResponse}
                  performingLab={labOrder.state.performingLab}
                  selectedTests={labOrder.state.selectedTests}
                  testMetadata={labOrder.state.testMetadata}
                  searchAvailableLabs={labOrder.searchAvailableLabs}
                  searchAvailableTests={labOrder.searchAvailableTests}
                  onQuestionnaireChange={handleQuestionnaireChange}
                  onLabChange={handleLabsChange}
                  onTestsChange={handleTestsChange}
                  isAutoSelectingTests={isAutoSelectingTests}
                  orderingProviderField={
                    <Select
                      label="Ordering provider"
                      description={
                        orderingProvider && orderingProvider.id !== profile.id
                          ? 'The order is placed under this provider’s NPI'
                          : undefined
                      }
                      placeholder={isLoadingProviders ? 'Loading providers…' : 'Choose a provider'}
                      data={providerOptions}
                      value={orderingProvider?.id ?? null}
                      onChange={setPickedProviderId}
                      searchable
                      required
                      disabled={isLoadingProviders}
                      nothingFoundMessage="No provider with an NPI"
                    />
                  }
                />

                <Flex justify="flex-end" gap="sm">
                  <Button variant="outline" onClick={() => setShowMessageInput(false)} radius="xl">
                    Cancel
                  </Button>
                  <Button
                    variant="filled"
                    color="teal"
                    onClick={handleSubmit}
                    disabled={
                      !orderingProvider ||
                      !labOrder.state.performingLab ||
                      labOrder.state.selectedTests.length === 0 ||
                      isSubmitting
                    }
                    radius="xl"
                  >
                    {isSubmitting ? <Loader size="sm" color="white" /> : 'Create Lab Order'}
                  </Button>
                </Flex>
              </Stack>
            </HealthGorillaLabOrderProvider>
          ) : (
            <Text c="red.6">No lab order form available.</Text>
          )}
        </Box>
      </Box>
    </Card>
  );
}
